package ca.northroot.app.data

import kotlinx.serialization.Serializable

@Serializable
data class UserProfile(
    val uid: String = "",
    val email: String = "",
    val displayName: String = "",
    val phone: String = "",
    val role: UserRole = UserRole.USER,
    val companyId: String = "",
    val fcmToken: String = "",
    val hourlyWage: Double = 0.0,
    val isActive: Boolean = true,
    val lastLatitude: Double? = null,
    val lastLongitude: Double? = null,
    val lastLocationUpdate: Long? = null,
    val isLocationTrackingEnabled: Boolean = true,
    val isReportingLocation: Boolean = false
)

@Serializable
enum class UserRole {
    OWNER,
    ADMIN,
    USER
}

@Serializable
data class Company(
    val id: String = "",
    val name: String = "",
    val ownerId: String = "",
    val logoUrl: String = "",
    val phone: String = "",
    val email: String = "",
    val website: String = "",
    val address: String = "",
    val createdAt: Long? = null,
    val ownerUID: String = "",
    val onTheWayMessage: String = "Hi {customer}, Northroot is on the way for your {service}!",
    val startJobMessage: String = "Hi {customer}, your service {service} has started!",
    val completeJobMessage: String = "Hi {customer}, your service {service} is complete. Have a great day!"
)

@Serializable
data class Customer(
    val id: String = "",
    val firstName: String = "",
    val lastName: String = "",
    val name: String = "",
    val email: String = "",
    val phone: String = "",
    val address: String = "",
    val latitude: Double? = null,
    val longitude: Double? = null,
    val unit: String = "",
    val city: String = "",
    val province: String = "",
    val postalCode: String = "",
    val country: String = "Canada",
    val notes: String = "",
    val propertyImageUrl: String = "",
    val shareToken: String = "",
    val createdAt: Long? = null
)

@Serializable
data class Job(
    val id: String = "",
    val customerId: String = "",
    val customerName: String = "",
    val serviceType: String = "",
    var scheduledDate: Long? = null,
    var scheduledEndDate: Long? = null,
    val status: JobStatus = JobStatus.PENDING,
    val price: Double = 0.0,
    val notes: String = "",
    val startedAt: Long? = null,
    val startedBy: String = "",
    val startedByName: String = "",
    val completedAt: Long? = null,
    val completedBy: String = "",
    val completedByName: String = "",
    var assignedTo: List<String> = emptyList(),
    var assignedNames: List<String> = emptyList(),
    var isRecurring: Boolean = false,
    val recurringWeeks: Int = 0,
    var recurringUntil: Long? = null,
    val parentJobId: String? = null,
    val recurringGroupId: String? = null,
    val rescheduleReason: String? = null,
    val originalScheduledDate: Long? = null,
    val delayReason: String? = null
)

@Serializable
enum class JobStatus {
    PENDING,
    IN_PROGRESS,
    COMPLETED,
    CANCELLED,
    INVOICED,
    SKIPPED
}

@Serializable
data class Service(
    val id: String = "",
    val name: String = "",
    val defaultPrice: Double = 0.0
)

@Serializable
data class Invoice(
    val id: String = "",
    val invoiceNumber: String = "",
    val customerId: String = "",
    val customerName: String = "",
    val customerAddress: String = "",
    val date: Long? = null,
    val dueDate: Long? = null,
    val paymentTerms: String = "Due on receipt",
    val items: List<InvoiceItem> = emptyList(),
    val subtotal: Double = 0.0,
    val discountType: String = "FLAT",
    val discountValue: Double = 0.0,
    val discountAmount: Double = 0.0,
    val taxName: String = "HST",
    val taxRate: Double = 0.0,
    val taxAmount: Double = 0.0,
    val depositType: String = "FLAT",
    val depositValue: Double = 0.0,
    val depositAmount: Double = 0.0,
    val totalAmount: Double = 0.0,
    val paidAmount: Double = 0.0,
    val payments: List<PaymentRecord> = emptyList(),
    val paymentSchedule: List<PaymentScheduleItem> = emptyList(),
    val paymentInstructions: String = "",
    val termsAndConditions: String = "",
    val status: InvoiceStatus = InvoiceStatus.UNPAID,
    val notes: String = "",
    val propertyImageUrl: String = ""
)

@Serializable
data class PaymentRecord(
    val amount: Double = 0.0,
    val method: PaymentMethod = PaymentMethod.CASH,
    val date: Long? = null,
    val notes: String = ""
)

@Serializable
enum class PaymentMethod {
    CASH, E_TRANSFER, CHEQUE, CREDIT_CARD
}

@Serializable
data class InvoiceItem(
    val description: String = "",
    val quantity: Double = 1.0,
    val unitPrice: Double = 0.0,
    val amount: Double = 0.0
)

@Serializable
data class PaymentScheduleItem(
    val description: String = "",
    val amount: Double = 0.0,
    val dueDate: Long? = null,
    var isPaid: Boolean = false
)

@Serializable
enum class InvoiceStatus {
    DRAFT,
    UNPAID,
    PAID,
    OVERDUE,
    CANCELLED
}
